import type { RouterOutputs } from '@poseidon/api/types';
import type { DateTime } from 'luxon';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import type { TeamLogo } from '@poseidon/components/Avatar';
import type { CampaignTemplateOption, UserOption } from '@poseidon/components/Input2';
import {
  Button,
  ButtonGroup,
  CampaignTemplateFilterSelect,
  CompanyFilterSelect,
  DateButton,
  FilterSelect,
  OrderFilterSelect,
  OwnerFilterSelect,
  ResponseFilterSelect,
  StageFilterSelect,
  StatesFilterSelect,
  TagFilterSelect,
  TEMPLATE_SORT_DEFAULT_VALUE,
  TemplateSortFilterSelect,
} from '@poseidon/components/Input2';
import type { CompanyOption } from '@poseidon/components/Input2';
import { Communication, Files, General, Maps, Time, Users } from '@poseidon/components/StrokeIcons';

type FilterStage = NonNullable<RouterOutputs['filters']['data']['stages']>[number];

import type { PickerTeamUser } from './FilterPickers';
import {
  ConnectedUserFilter,
  SignalMultiFilter,
  STAGE_ALL_OPTION_VALUE,
  StageFilter,
  StageMultiFilter,
} from './FilterControls';
import type { CandidateStatusValue } from './Filters';
import { CANDIDATE_STATUS_ALL_VALUE, CANDIDATE_STATUS_OPTIONS } from './Filters';
import type { FilterShow } from './types';
import StepsRunCountFilter from './StepsRunCountFilter';
import type { SearchParams } from '@/hooks/useRouterQuery';

// Template-activity filter: client-side tier on /templates (Active / Idle /
// Stale / No activity). The empty string means "All".
type TemplateActivityValue = 'active' | 'idle' | 'stale' | 'no_activity';

const TEMPLATE_ACTIVITY_OPTIONS: { value: TemplateActivityValue; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'idle', label: 'Idle' },
  { value: 'stale', label: 'Stale' },
  { value: 'no_activity', label: 'No activity' },
];

const TEMPLATE_ACTIVITY_ALL_VALUE = '';

// Shared owner dropdown used by both the contact-owner (`userId`) and
// campaign-owner (`cadenceUserId`) filters. The value `'team'` renders the
// team name + logo (the "whole team / nobody specific" default).
function TeamOwnerSelect({
  userOptions,
  teamName,
  teamLogo,
  value,
  onChange,
  tooltip,
}: {
  userOptions: UserOption[];
  teamName: string;
  teamLogo?: TeamLogo | null;
  value: string;
  onChange: (value: string) => void;
  tooltip: string;
}) {
  return (
    <OwnerFilterSelect
      size="sm"
      userOptions={userOptions}
      teamOption={{
        label: teamName,
        icon: (
          <TeamUserAvatar
            userTeam={{ profilePicture: teamLogo?.url, userTeamName: teamName }}
            className="size-full"
            shape="circle"
          />
        ),
      }}
      value={value}
      defaultValue="team"
      onChange={onChange}
      tooltip={tooltip}
    />
  );
}

export type FilterChipProps = {
  filter: FilterShow;
  teamName: string;
  userId: string;
  teamLogo?: TeamLogo | null;
  userIdLabel?: string;
  query: SearchParams;
  userOptions: UserOption[];
  tagOptions: { value: string; label: string }[];
  contactTagOptions: { value: string; label: string }[];
  templates: CampaignTemplateOption[];
  stages: FilterStage[];
  orgOptions?: CompanyOption[];
  teamUsers: PickerTeamUser[] | null;
  filtersLoading: boolean;
  fromDate: DateTime;
  toDate: DateTime;
  candidateStatusCounts?: Partial<Record<CandidateStatusValue, number>>;
  enrollmentCounts?: { active: number; all: number };
  onUpdateFilters: (changes: Partial<SearchParams>) => void;
  onDateRangeChange: (range: { from?: string; to?: string }) => void;
};

/**
 * Renders a single active filter chip in the Filters2 toolbar. Extracted from
 * the former `renderFilter()` helper inside `Filters2` — one `FilterShow`
 * discriminant maps to one chip. Every value the chip needs (query, callbacks,
 * option lists) is threaded in as an explicit prop rather than closed over.
 */
export function FilterChip({
  filter,
  teamName,
  userId,
  teamLogo,
  userIdLabel,
  query,
  userOptions,
  tagOptions,
  contactTagOptions,
  templates,
  stages,
  orgOptions,
  teamUsers,
  filtersLoading,
  fromDate,
  toDate,
  candidateStatusCounts,
  enrollmentCounts,
  onUpdateFilters,
  onDateRangeChange,
}: FilterChipProps) {
  switch (filter) {
    case 'userId':
      if (userIdLabel == null) {
        return (
          <TeamOwnerSelect
            userOptions={userOptions}
            teamName={teamName}
            teamLogo={teamLogo}
            value={query.userId || 'team'}
            onChange={(value) => onUpdateFilters({ userId: value === 'team' ? undefined : value })}
            tooltip="Change owner"
          />
        );
      }
      return (
        <ButtonGroup size="sm">
          <Button readOnly icon={<Users.User01 />}>
            {userIdLabel}
          </Button>
          <TeamOwnerSelect
            userOptions={userOptions}
            teamName={teamName}
            teamLogo={teamLogo}
            value={query.userId || 'team'}
            onChange={(value) => onUpdateFilters({ userId: value === 'team' ? undefined : value })}
            tooltip="Change owner"
          />
        </ButtonGroup>
      );
    case 'cadenceUserId':
      return (
        <ButtonGroup size="sm">
          <Button readOnly icon={<Maps.Rocket />}>
            Campaign owner
          </Button>
          <TeamOwnerSelect
            userOptions={userOptions}
            teamName={teamName}
            teamLogo={teamLogo}
            value={query.cadenceUserId || 'team'}
            onChange={(value) => onUpdateFilters({ cadenceUserId: value === 'team' ? undefined : value })}
            tooltip="Filter by campaign owner"
          />
        </ButtonGroup>
      );
    case 'connectedUserId': {
      const value = query.connectedUserId;
      if (value == null) return null;
      if (value === '__any__') {
        return (
          <ButtonGroup size="sm">
            <Button icon={<Users.Users01 />}>Connected to Anyone</Button>
            <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ connectedUserId: undefined })} />
          </ButtonGroup>
        );
      }
      return (
        <ButtonGroup size="sm">
          <Button icon={<Users.UserPlus01 />}>Connected to</Button>
          <ConnectedUserFilter
            size="sm"
            userId={userId}
            value={value}
            teamUsers={teamUsers}
            isPending={filtersLoading}
            onChange={({ value }) => onUpdateFilters({ connectedUserId: value })}
          />
          <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ connectedUserId: undefined })} />
        </ButtonGroup>
      );
    }
    case 'connectionAccepted': {
      const value = query.connectionAccepted;
      if (value == null) return null;
      return (
        <ButtonGroup size="sm">
          <Button icon={<Users.UserPlus01 />}>{value === 'accepted' ? 'Connected' : 'Connection Sent'}</Button>
          <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ connectionAccepted: undefined })} />
        </ButtonGroup>
      );
    }
    case 'connectionType': {
      const value = query.connectionType;
      if (value == null || value === 'all') return null;
      return (
        <ButtonGroup size="sm">
          <Button icon={<Communication.UserCheckSquare />}>
            {value === 'external' ? 'External Connection' : 'Connected via Poseidon'}
          </Button>
          <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ connectionType: undefined })} />
        </ButtonGroup>
      );
    }
    case 'stepsRunCount': {
      const count = query.stepsRunCount;
      if (count == null) return null;
      return (
        <ButtonGroup size="sm">
          <Button icon={<General.Hash01 />}>Steps Run</Button>
          <StepsRunCountFilter
            size="sm"
            count={count}
            operation={query.stepsRunCountOperation ?? '=='}
            onChange={(count: number, operation: '==' | '<=' | '>=') =>
              onUpdateFilters({ stepsRunCount: count, stepsRunCountOperation: operation })
            }
          />
          <Button
            icon={<General.XMark />}
            onClick={() => onUpdateFilters({ stepsRunCount: undefined, stepsRunCountOperation: undefined })}
          />
        </ButtonGroup>
      );
    }
    case 'tag': {
      const value = query.tag ?? '';
      if (!value) return null;
      return (
        <TagFilterSelect
          size="sm"
          tagOptions={tagOptions}
          value={value}
          defaultValue=""
          onChange={(value) => onUpdateFilters({ tag: value || undefined })}
        />
      );
    }
    case 'contactTag': {
      const value = query.tag ?? '';
      if (!value) return null;
      return (
        <TagFilterSelect
          size="sm"
          tagOptions={contactTagOptions}
          value={value}
          defaultValue=""
          onChange={(value) => onUpdateFilters({ tag: value || undefined })}
        />
      );
    }
    case 'templateId': {
      const value = query.templateId ?? '';
      return (
        <CampaignTemplateFilterSelect
          size="sm"
          templates={templates}
          includeFilterOptions
          renderOwnerIcon={(template) => (
            <TeamUserAvatar
              userTeam={{ profilePicture: template.ownerProfilePicture, userTeamName: template.ownerName }}
              className="size-full"
              shape="circle"
            />
          )}
          value={value}
          defaultValue=""
          onChange={(value) => onUpdateFilters({ templateId: value || undefined })}
        />
      );
    }
    case 'stageId': {
      const value = query.stageId;
      return (
        <ButtonGroup size="sm">
          <Button tooltip="Stage" icon={<Files.StickerCircle />} />
          <StageFilter
            size="sm"
            value={value}
            stages={stages}
            isPending={filtersLoading}
            onChange={({ value }) => {
              onUpdateFilters({ stageId: value === STAGE_ALL_OPTION_VALUE ? undefined : value });
            }}
          />
        </ButtonGroup>
      );
    }
    case 'stageId2': {
      const value = query.stageId ?? '';
      return (
        <StageFilterSelect
          size="sm"
          stages={stages}
          value={value}
          defaultValue=""
          onChange={(value) => {
            onUpdateFilters({ stageId: value || undefined });
          }}
        />
      );
    }
    case 'stageIds': {
      const value = query.stageIds ?? [];
      if (value.length === 0) return null;
      return (
        <ButtonGroup size="sm">
          <Button tooltip="Stage" icon={<Files.StickerCircle />} />
          <StageMultiFilter
            size="sm"
            value={value}
            stages={stages}
            isPending={filtersLoading}
            onChange={(next) => onUpdateFilters({ stageIds: next.length > 0 ? next : undefined })}
          />
          <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ stageIds: undefined })} />
        </ButtonGroup>
      );
    }
    case 'signalBuckets': {
      const value = query.signalBuckets ?? [];
      if (value.length === 0) return null;
      return (
        <ButtonGroup size="sm">
          <Button tooltip="Signal" icon={<Communication.Rss01 />} />
          <SignalMultiFilter
            size="sm"
            value={value}
            onChange={(next) => onUpdateFilters({ signalBuckets: next.length > 0 ? next : undefined })}
          />
          <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ signalBuckets: undefined })} />
        </ButtonGroup>
      );
    }
    case 'companyId': {
      const value = query.companyId ?? '';
      if (!value) return null;
      return (
        <CompanyFilterSelect
          size="sm"
          companyOptions={orgOptions ?? []}
          value={value}
          defaultValue=""
          onChange={(value) => onUpdateFilters({ companyId: value || undefined })}
        />
      );
    }
    case 'dateRange':
      return (
        <ButtonGroup size="sm">
          <Button icon={<Time.CalendarDate />} />
          <DateButton
            size="sm"
            value={fromDate.valueOf()}
            onDateChange={(from: string) => onDateRangeChange({ from })}
            hideCurrentYear
            icon={null}
          />
          <DateButton
            size="sm"
            value={toDate.valueOf()}
            onDateChange={(to: string) => onDateRangeChange({ to })}
            hideCurrentYear
            icon={null}
          />
        </ButtonGroup>
      );
    case 'order': {
      const value = query.order ?? '';
      if (!value) return null;
      return (
        <OrderFilterSelect
          size="sm"
          value={value}
          defaultValue=""
          onChange={(value) => onUpdateFilters({ order: value ? (value as SearchParams['order']) : undefined })}
        />
      );
    }
    case 'states': {
      const value = query.states ?? '';
      if (!value) return null;
      return (
        <StatesFilterSelect
          size="sm"
          value={value}
          defaultValue=""
          onChange={(value) => onUpdateFilters({ states: value ? (value as SearchParams['states']) : undefined })}
        />
      );
    }
    case 'response': {
      const value = query.response ?? '';
      if (!value) return null;
      return (
        <ResponseFilterSelect
          size="sm"
          value={value}
          defaultValue=""
          onChange={(value) => onUpdateFilters({ response: value ? (value as SearchParams['response']) : undefined })}
        />
      );
    }
    case 'candidateStatus': {
      const value = query.candidateStatus ?? CANDIDATE_STATUS_ALL_VALUE;
      const options = [
        { value: CANDIDATE_STATUS_ALL_VALUE, label: 'All statuses' },
        ...CANDIDATE_STATUS_OPTIONS.map((o) => ({
          value: o.value,
          label: `${o.label} (${candidateStatusCounts?.[o.value] ?? 0})`,
        })),
      ];
      return (
        <FilterSelect
          size="sm"
          options={options}
          value={value}
          placeholder="Staging status"
          onChange={(v) =>
            onUpdateFilters({
              candidateStatus: v === CANDIDATE_STATUS_ALL_VALUE ? undefined : (v as CandidateStatusValue),
            })
          }
          filterable={false}
          autoWidth
        />
      );
    }
    case 'templateActivity': {
      const value = query.templateActivity ?? TEMPLATE_ACTIVITY_ALL_VALUE;
      const options = [{ value: TEMPLATE_ACTIVITY_ALL_VALUE, label: 'All campaigns' }, ...TEMPLATE_ACTIVITY_OPTIONS];
      return (
        <FilterSelect
          size="sm"
          options={options}
          value={value}
          placeholder="Activity"
          onChange={(v) =>
            onUpdateFilters({
              templateActivity: v === TEMPLATE_ACTIVITY_ALL_VALUE ? undefined : (v as TemplateActivityValue),
              offset: undefined,
            })
          }
          filterable={false}
          autoWidth
        />
      );
    }
    case 'enrollment': {
      // Coarse scope toggle for the enrolled list. 'active' (default) shows each
      // contact's one live cadence; 'all' reveals every non-deleted enrollment.
      // Always visible so the user can always see — and switch — which set they're
      // viewing. The 'All' segment is the way back to the full list.
      const value = query.enrollment ?? 'active';
      const activeLabel = enrollmentCounts ? `Active · ${enrollmentCounts.active}` : 'Active';
      const allLabel = enrollmentCounts ? `All · ${enrollmentCounts.all}` : 'All';
      return (
        <ButtonGroup size="sm">
          <Button
            icon={<Users.Users01 />}
            color={value === 'active' ? 'blue' : 'gray'}
            tone={value === 'active' ? 'filled' : 'soft'}
            onClick={() => onUpdateFilters({ enrollment: undefined })}
            tooltip="People currently in this campaign — each contact's one active cadence">
            {activeLabel}
          </Button>
          <Button
            color={value === 'all' ? 'blue' : 'gray'}
            tone={value === 'all' ? 'filled' : 'soft'}
            onClick={() => onUpdateFilters({ enrollment: 'all' })}
            tooltip="Everyone ever enrolled, including finished, paused, and past re-enrollments">
            {allLabel}
          </Button>
        </ButtonGroup>
      );
    }
    case 'templateSort':
      return (
        <TemplateSortFilterSelect
          size="sm"
          value={query.templateSort ?? TEMPLATE_SORT_DEFAULT_VALUE}
          defaultValue={TEMPLATE_SORT_DEFAULT_VALUE}
          onChange={(value) =>
            onUpdateFilters({
              templateSort:
                value === TEMPLATE_SORT_DEFAULT_VALUE ? undefined : (value as SearchParams['templateSort']),
              // `order` is only set by the Created column header; a dropdown pick
              // returns to that option's natural direction.
              order: undefined,
            })
          }
        />
      );
    case 'recentlyConnected': {
      const isActive = query.recentlyConnected === true;
      return (
        <ButtonGroup size="sm">
          <Button
            onClick={() => onUpdateFilters({ recentlyConnected: isActive ? undefined : true })}
            icon={<Time.Clock />}
            tooltip="Contacts connected in the last 7 days">
            Recent
          </Button>
          {isActive && (
            <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ recentlyConnected: undefined })} />
          )}
        </ButtonGroup>
      );
    }
    case 'messagesSentMax': {
      const value = query.messagesSentMax;
      if (!value) return null;
      return (
        <ButtonGroup size="sm">
          <FilterSelect
            size="sm"
            icon={<Communication.MessageSquare01 />}
            options={[
              { value: '1', label: '≤ 1 message' },
              { value: '2', label: '≤ 2 messages' },
              { value: '3', label: '≤ 3 messages' },
              { value: '4+', label: '4+ messages' },
            ]}
            value={value}
            onChange={(newValue) => {
              if (newValue === '1' || newValue === '2' || newValue === '3' || newValue === '4+') {
                onUpdateFilters({ messagesSentMax: newValue });
              }
            }}
            tooltip="Messages Sent"
          />
          <Button icon={<General.XMark />} onClick={() => onUpdateFilters({ messagesSentMax: undefined })} />
        </ButtonGroup>
      );
    }
    default:
      return null;
  }
}
